import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Plus, MagnifyingGlass, Trash, ArrowCounterClockwise } from '@phosphor-icons/react';

export default function SaleReturnPage() {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);

  const load = () => { api.getSaleReturns().then(r => { setReturns(r.data.returns || []); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Sale Returns</h1>
          <p className="text-sm mt-1" style={{ color: '#8A94A6' }}>Manage return/credit notes</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="h-9 px-4 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} className="mr-1" /> New Return
        </Button>
      </div>

      <div className="rounded-md border overflow-hidden" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
        <table className="w-full text-sm" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <thead><tr style={{ background: '#F9F9FA' }}>
            {['Return #', 'Date', 'Original Invoice', 'Customer', 'Items', 'Amount', 'Reason'].map(h => (
              <th key={h} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>Loading...</td></tr> :
              returns.length === 0 ? <tr><td colSpan={7} className="px-4 py-8 text-center" style={{ color: '#8A94A6' }}>No returns recorded</td></tr> :
              returns.map((r, i) => (
                <tr key={i} className="hover:bg-slate-50" style={{ borderBottom: '1px solid #E2E4E9' }}>
                  <td className="px-4 py-2 font-medium" style={{ color: '#FF3B30' }}>{r.return_number}</td>
                  <td className="px-4 py-2 text-xs" style={{ color: '#8A94A6' }}>{r.date}</td>
                  <td className="px-4 py-2 text-xs" style={{ color: '#007AFF' }}>{r.original_invoice || '-'}</td>
                  <td className="px-4 py-2">{r.customer_name}</td>
                  <td className="px-4 py-2" style={{ fontVariantNumeric: 'tabular-nums' }}>{r.total_qty}</td>
                  <td className="px-4 py-2 font-medium" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{(r.total_amount || 0).toLocaleString()}</td>
                  <td className="px-4 py-2 text-xs" style={{ color: '#8A94A6' }}>{r.reason}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>New Sale Return</DialogTitle></DialogHeader>
          <SaleReturnForm onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SaleReturnForm({ onSave }) {
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [medSearch, setMedSearch] = useState('');
  const [medicines, setMedicines] = useState([]);
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState('');
  const [originalInvoice, setOriginalInvoice] = useState('');
  const [showMedDropdown, setShowMedDropdown] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { api.getCustomers().then(r => setCustomers(r.data)); }, []);
  useEffect(() => {
    if (medSearch.length >= 1) { api.getMedicines(medSearch).then(r => { setMedicines(r.data); setShowMedDropdown(true); }); }
    else { setShowMedDropdown(false); }
  }, [medSearch]);

  const addItem = (med) => {
    setItems([...items, { medicine_id: med.id, medicine_name: med.name, batch_number: med.batch_number || '', qty: 1, mrp: med.mrp, amount: med.mrp }]);
    setMedSearch(''); setShowMedDropdown(false);
  };
  const updateItem = (idx, field, value) => {
    setItems(items.map((item, i) => {
      if (i !== idx) return item;
      const updated = { ...item, [field]: value };
      if (['qty', 'mrp'].includes(field)) updated.amount = Number(updated.qty) * Number(updated.mrp);
      return updated;
    }));
  };

  const totalAmount = items.reduce((s, i) => s + i.amount, 0);

  const save = async () => {
    if (items.length === 0) return;
    setSaving(true);
    try {
      await api.createSaleReturn({
        original_invoice: originalInvoice,
        customer_id: selectedCustomer?.id || null,
        customer_name: selectedCustomer?.name || '',
        items, reason,
        date: new Date().toISOString().split('T')[0]
      });
      onSave();
    } catch {}
    setSaving(false);
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Original Invoice #</Label>
          <Input value={originalInvoice} onChange={e => setOriginalInvoice(e.target.value)} className="h-8 mt-1" placeholder="e.g. INV-2025-1001" />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Customer</Label>
          <select className="w-full h-8 mt-1 border rounded px-2 text-sm" style={{ borderColor: '#E2E4E9' }} onChange={e => setSelectedCustomer(customers.find(c => c.id === e.target.value))}>
            <option value="">Select customer</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>
      <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: '#8A94A6' }} />
        <Input value={medSearch} onChange={e => setMedSearch(e.target.value)} placeholder="Search medicine to return..." className="pl-9 h-8" />
        {showMedDropdown && medicines.length > 0 && (
          <div className="absolute z-20 mt-1 w-full border rounded shadow-lg max-h-40 overflow-y-auto" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
            {medicines.map(m => (
              <button key={m.id} onClick={() => addItem(m)} className="w-full text-left px-3 py-2 text-xs hover:bg-slate-50" style={{ borderBottom: '1px solid #E2E4E9' }}>
                {m.name} <span style={{ color: '#8A94A6' }}>| MRP: ₹{m.mrp}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {items.length > 0 && (
        <div className="rounded-md border overflow-hidden" style={{ borderColor: '#E2E4E9' }}>
          <table className="w-full text-xs">
            <thead><tr style={{ background: '#FF3B30' }}>
              {['Medicine', 'Batch', 'Qty', 'MRP', 'Amount', ''].map(h => <th key={h} className="px-3 py-1.5 text-left text-white font-semibold">{h}</th>)}
            </tr></thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx} style={{ borderBottom: '1px solid #E2E4E9' }}>
                  <td className="px-3 py-1.5 font-medium">{item.medicine_name}</td>
                  <td className="px-3 py-1.5 text-xs" style={{ color: '#8A94A6' }}>{item.batch_number}</td>
                  <td className="px-3 py-1.5"><Input type="number" value={item.qty} onChange={e => updateItem(idx, 'qty', Number(e.target.value))} className="h-6 w-14 text-xs" min="1" /></td>
                  <td className="px-3 py-1.5 text-right" style={{ fontVariantNumeric: 'tabular-nums' }}>₹{item.mrp}</td>
                  <td className="px-3 py-1.5 text-right font-medium" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{item.amount.toFixed(2)}</td>
                  <td className="px-3 py-1.5"><button onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash size={12} style={{ color: '#FF3B30' }} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Reason for Return</Label>
        <Input value={reason} onChange={e => setReason(e.target.value)} className="h-8 mt-1" placeholder="e.g. Expired, Damaged, Wrong item" /></div>
      <div className="flex justify-between items-center p-3 rounded-md" style={{ background: '#FFF0F0' }}>
        <span className="text-xs font-semibold" style={{ color: '#FF3B30' }}>Total Return Amount</span>
        <span className="text-lg font-bold" style={{ color: '#FF3B30', fontVariantNumeric: 'tabular-nums' }}>₹{totalAmount.toLocaleString()}</span>
      </div>
      <Button onClick={save} disabled={saving || items.length === 0} className="w-full h-9 text-sm font-semibold" style={{ background: '#FF3B30', color: 'white' }}>
        <ArrowCounterClockwise size={16} className="mr-1" /> {saving ? 'Processing...' : 'Process Return'}
      </Button>
    </div>
  );
}
